// What can be drawn on a dental image, and the arithmetic for drawing it.
//
// ────────────────────────────────────────────────────────────────────────────
// EVERY COORDINATE IN THIS FILE IS NORMALIZED TO [0,1]
// ────────────────────────────────────────────────────────────────────────────
// Not pixels. Never pixels. The same radiograph is drawn at 900px on a
// reception monitor, at 380px on a phone, and at whatever the current zoom
// makes it — and it is re-drawn on every one of those changes. A mark stored in
// pixels is a mark that lands on a different tooth the moment the window is
// resized, and the dentist who drew it is the last person who would ever see it
// in the right place.
//
// The server enforces the same range (dental_image_links, validate_annotations)
// so a client that forgets cannot store one either. The conversion happens in
// exactly two functions below — `toUnit` on the way in from a pointer event,
// and `toPercent` on the way out to CSS — and nowhere else.
//
// It lives in lib/ rather than inside the workspace component because the
// annotation layer, the findings layer and the hit-test all need the same
// arithmetic, and three copies of "is this point inside that box" is how two of
// them end up disagreeing about the edge.

import { C, CT } from '../constants/theme';

// ── The vocabulary ──────────────────────────────────────────────────────────
//
// Closed, and identical to the server's ANNOTATION_KINDS. A kind this file
// invents is a mark the save rejects — after the dentist drew it.

export const MARK_PIN = 'pin';
export const MARK_BOX = 'box';
export const MARK_ELLIPSE = 'ellipse';
export const MARK_ARROW = 'arrow';
export const MARK_FREEHAND = 'freehand';

export const MARK_KINDS = [MARK_PIN, MARK_BOX, MARK_ELLIPSE, MARK_ARROW, MARK_FREEHAND];

/** Kinds that are dragged out rather than placed with one click. */
export const DRAGGED_KINDS = new Set([MARK_BOX, MARK_ELLIPSE, MARK_ARROW, MARK_FREEHAND]);

/** Kinds that carry a width and a height. */
export const SIZED_KINDS = new Set([MARK_BOX, MARK_ELLIPSE]);

/**
 * The pen colours.
 *
 * Four, and no picker. A colour picker on a clinical annotation tool invites a
 * private code — "I use green for watch" — that only its author can read, and
 * the next dentist to open the image sees a green circle meaning nothing. Four
 * named choices stay legible to a colleague.
 */
export const MARK_COLORS = ['#E11D48', '#F59E0B', '#0EA5A0', '#2563EB'];
export const DEFAULT_MARK_COLOR = MARK_COLORS[0];

/**
 * The ceilings, mirrored from the server so the refusal happens before the save.
 *
 * A dentist who draws a 201st mark should be told at the pen, not after
 * pressing ذخیره and losing the lot — the save is whole-array, so a rejection
 * takes every mark with it.
 */
export const MAX_MARKS = 200;
export const MAX_COMMENT_CHARS = 2000;
export const MAX_STROKE_POINTS = 2000;

// ── The image itself ────────────────────────────────────────────────────────

/**
 * What a dental image can BE. The dental service's closed set — a view outside
 * it is refused at upload, and it is what tells the model which findings the
 * film can support at all.
 *
 * `photo` last: it is the one that is not a radiograph, and the ordering keeps
 * the three x-ray views a dentist picks between adjacent.
 */
export const VIEW_TYPES = ['periapical', 'bitewing', 'panoramic', 'ceph', 'photo'];

/**
 * Which uploads are IMAGES.
 *
 * A PDF referral is a document: it gets no view type, no annotation canvas and
 * no image reading — and that is not a limitation, it is the correct answer.
 * Registering it as a `photo` so the buttons light up would hand the model a
 * scanned letter and ask it what teeth it shows.
 */
export const isImageType = (t) => /^image\//i.test(String(t || ''));

/**
 * Types whose PREVIEW this app can actually render.
 *
 * HEIC is the trap. `accept="image/*"` admits it, an iPhone produces it by
 * default, the upload succeeds — and then no browser but Safari draws it, so
 * the dentist gets a blank frame with no error anywhere. Better to say so at
 * the picker than to store bytes nobody can look at.
 */
export const RENDERABLE_IMAGE = /^image\/(jpeg|png|gif|bmp|webp|tiff)$/i;
export const isRenderableImage = (t) => RENDERABLE_IMAGE.test(String(t || ''));

// ── The arithmetic ──────────────────────────────────────────────────────────

export const clamp01 = (n) => (n < 0 ? 0 : n > 1 ? 1 : n);

/**
 * A pointer event → a point in the image's own frame.
 *
 * `getBoundingClientRect` and not `offsetX/offsetY`: the latter is relative to
 * whichever element the pointer happens to be over, which during a drag is
 * sometimes a mark and sometimes the image, and the coordinate would jump
 * between them mid-stroke.
 */
export function toUnit(event, element) {
  if (!element) return { x: 0, y: 0 };
  const rect = element.getBoundingClientRect();
  if (!rect.width || !rect.height) return { x: 0, y: 0 };
  return {
    x: clamp01((event.clientX - rect.left) / rect.width),
    y: clamp01((event.clientY - rect.top) / rect.height),
  };
}

/** A unit coordinate → the CSS percentage that positions it. */
export const toPercent = (n) => `${(clamp01(n) * 100).toFixed(4)}%`;

/**
 * A drag → the geometry of the kind being drawn.
 *
 * A box is normalized so that dragging up-and-left produces the same rectangle
 * as dragging down-and-right; without this, half of every dentist's boxes have
 * a negative width and vanish.
 */
export function geometryFor(kind, from, to) {
  if (kind === MARK_ARROW) {
    return { x: from.x, y: from.y, x2: to.x, y2: to.y };
  }
  if (SIZED_KINDS.has(kind)) {
    return {
      x: Math.min(from.x, to.x),
      y: Math.min(from.y, to.y),
      w: Math.abs(to.x - from.x),
      h: Math.abs(to.y - from.y),
    };
  }
  return { x: to.x, y: to.y };
}

/**
 * Is this drag big enough to have been meant?
 *
 * A click that moves two pixels while placing a box would otherwise store a
 * zero-area rectangle — invisible on screen, counted against the ceiling, and
 * refused by the server, which requires a non-zero width and height.
 */
export const MIN_DRAG = 0.01;

export function isDragMeaningful(kind, from, to) {
  if (!DRAGGED_KINDS.has(kind)) return true;
  return Math.abs(to.x - from.x) > MIN_DRAG || Math.abs(to.y - from.y) > MIN_DRAG;
}

/** A new mark, in the shape the server stores. */
export function newMark({ kind, geometry, color, toothFdi }) {
  return {
    // A client-side id, so a mark can be selected, edited and erased before it
    // has ever been saved. The server keeps whatever id it is given.
    id: `m-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    kind,
    x: 0, y: 0,
    ...geometry,
    points: geometry.points || [],
    toothFdi: toothFdi || null,
    comment: '',
    color: color || DEFAULT_MARK_COLOR,
    // authorId and createdAtMs are the SERVER's to stamp. A client that set its
    // own would be asserting who drew a mark, which it is not in a position to
    // know — the token says who is logged in, not who is holding the mouse.
  };
}

/**
 * A stroke's flat [x0,y0,x1,y1,…] point list → an SVG polyline in the 0-100
 * viewBox the layers draw in.
 */
export function strokePoints(points) {
  const out = [];
  for (let i = 0; i + 1 < (points || []).length; i += 2) {
    out.push(`${clamp01(points[i]) * 100},${clamp01(points[i + 1]) * 100}`);
  }
  return out.join(' ');
}

/**
 * Where a mark's LABEL should sit — its anchor point, in unit coordinates.
 *
 * A box's anchor is its top-left rather than its centre, so a comment bubble
 * does not cover the thing the box was drawn around.
 */
export function anchorOf(mark) {
  if (!mark) return { x: 0, y: 0 };
  if (mark.kind === MARK_ARROW) return { x: mark.x2 ?? mark.x, y: mark.y2 ?? mark.y };
  return { x: mark.x, y: mark.y };
}

/**
 * Does a finding have a box worth drawing?
 *
 * All-zero geometry means the model saw something it could not localize. Such a
 * finding is listed BESIDE the image rather than drawn at the top-left corner —
 * a hotspot in the corner is a claim about where it is, and a wrong one.
 */
export const isLocalized = (f) => !!f && (f.w || 0) > 0 && (f.h || 0) > 0;

// ── How a finding looks ─────────────────────────────────────────────────────

/**
 * Confidence → the colour of its hotspot.
 *
 * Amber and grey for medium and low, teal for high, and NOT red for any of
 * them: red is the dentist's own pen and the danger token, and a model
 * proposal must never be the loudest thing on a radiograph.
 */
export function confidenceTone(confidence) {
  if (confidence === 'high') return { line: CT.accent, ink: CT.accentInk };
  if (confidence === 'medium') return { line: CT.warn, ink: CT.warnInk };
  return { line: C.textMuted, ink: C.textSecondary };
}

/**
 * Can this finding be confirmed onto the chart?
 *
 * Only one that names a tooth. A descriptive finding — «تجمع جرم در ناحیهٔ
 * قدامی پایین» — is a real observation about no single tooth, and there is
 * nothing for RecordToothEvents to write. The tick is hidden rather than
 * disabled: an always-refused control is worse than none.
 */
export const isConfirmable = (f) => !!(f?.toothFdi && f?.aspect && f?.value);
